/**
 * Presentational Profile screen (specs/profile-page). All data arrives as props so every
 * display state — populated, loading, error, no-friends — is story-testable in isolation
 * (§9 T-3/T-5/T-10/T-11). The container (ProfileScreen) wires the real hooks.
 *
 * The whole page scrolls as one region (no nested scroll).
 */

import React from 'react';
import { View, ScrollView, ActivityIndicator, Text, Pressable, type ImageSourcePropType } from 'react-native';
import type { DisplayAvatarId } from '../../../avatars/types/avatar.types';
import type { EquippedMap } from '../../../../components/avatars/LayeredAvatarLab/characterCustomizer.types';
import type { ProfileStatTile, ProfileXpDisplay, HeadToHeadRow } from '../../helpers/profileStats.helper';
import { ProfileHeaderBar } from '../ProfileHeaderBar/ProfileHeaderBar';
import { ProfileIdentity } from '../ProfileIdentity/ProfileIdentity';
import { ProfileStatsRow } from '../ProfileStatsRow/ProfileStatsRow';
import { DailyRewardsCard } from '../DailyRewardsCard/DailyRewardsCard';
import { HeadToHeadCard } from '../HeadToHeadCard/HeadToHeadCard';
import { PROFILE_COLORS, PROFILE_TEST_IDS } from './ProfileScreen.constants';
import { profileScreenStyles as styles } from './ProfileScreen.styles';

export interface ProfileScreenViewProps {
  displayName: string;
  rankName: string;
  tierColor: string;
  rankBadge: ImageSourcePropType;
  xp: ProfileXpDisplay | null;
  avatarId: DisplayAvatarId | null;
  avatarConfig: EquippedMap | null;
  statTiles: ProfileStatTile[];
  h2hRows: HeadToHeadRow[];
  /** True when the friends list failed to load — h2h shows retry, not empty. */
  h2hError: boolean;
  onRetryH2h: () => void;
  /** True while first-load is in flight with no data yet. */
  isLoading: boolean;
  /** True when a required fetch failed — show retry instead of fabricated zeros. */
  hasError: boolean;
  onRetry: () => void;
  onBack: () => void;
  onOpenSettings: () => void;
  onAvatarPress: () => void;
  onCollectDaily: () => void;
  /** Safe-area insets, passed by the container (kept as props so the view is provider-free in tests). */
  safeAreaTop?: number;
  safeAreaBottom?: number;
  testID?: string;
}

export const ProfileScreenView: React.FC<ProfileScreenViewProps> = ({
  displayName,
  rankName,
  tierColor,
  rankBadge,
  xp,
  avatarId,
  avatarConfig,
  statTiles,
  h2hRows,
  h2hError,
  onRetryH2h,
  isLoading,
  hasError,
  onRetry,
  onBack,
  onOpenSettings,
  onAvatarPress,
  onCollectDaily,
  safeAreaTop = 0,
  safeAreaBottom = 0,
  testID,
}) => {
  return (
    <View style={[styles.screen, { paddingTop: safeAreaTop }]} testID={testID ?? PROFILE_TEST_IDS.screen}>
      <ProfileHeaderBar onBack={onBack} onOpenSettings={onOpenSettings} />

      {hasError ? (
        <View style={styles.stateBlock}>
          <Text style={styles.stateText}>We couldn&apos;t load your profile.</Text>
          <Pressable
            onPress={onRetry}
            accessibilityRole="button"
            accessibilityLabel="Retry"
            testID={PROFILE_TEST_IDS.errorRetry}
            style={({ pressed }) => [styles.retryBtn, pressed && styles.retryBtnPressed]}
          >
            <Text style={styles.retryText}>Try again</Text>
          </Pressable>
        </View>
      ) : isLoading ? (
        <View style={styles.stateBlock}>
          <ActivityIndicator color={PROFILE_COLORS.purple} />
        </View>
      ) : (
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={[styles.scrollContent, { paddingBottom: safeAreaBottom + 24 }]}
          showsVerticalScrollIndicator={false}
          testID={PROFILE_TEST_IDS.scroll}
        >
          <ProfileIdentity
            displayName={displayName}
            rankName={rankName}
            tierColor={tierColor}
            rankBadge={rankBadge}
            xp={xp}
            avatarId={avatarId}
            avatarConfig={avatarConfig}
            onAvatarPress={onAvatarPress}
          />
          <ProfileStatsRow tiles={statTiles} />
          <DailyRewardsCard onCollect={onCollectDaily} />
          <HeadToHeadCard rows={h2hRows} hasError={h2hError} onRetry={onRetryH2h} />
        </ScrollView>
      )}
    </View>
  );
};
